/**
 * The ONE browser telemetry transport — every client event in every bundle
 * goes through track() to POST /api/events, where the single write channel
 * (src/edge/analytics/metadata/events.js) validates it against the registry.
 *
 * 铁血规则 (mirrors the channel's): 不分叉、不重复、单一数据源、单一读写通道。
 * No other beacon transport may exist in client code. No manual per-button
 * click beacons: ui.clicked comes ONLY from the delegated [data-log] listener
 * installed here. If a tap needs recording, it gets a data-log attribute and
 * its name goes into the registry vocabulary — never a new fetch call.
 *
 * Identity is server-owned: the browser sends NO ids except its per-tab
 * session id. visitor_id / person_id / click_id / is_internal are stamped
 * server-side from cookies the page never reads. Nothing here is consent-
 * gated — this is first-party essential measurement (the old consent-gated
 * posthog mirror is dead), and nothing here touches ad platforms.
 *
 * Events this module can emit (see registry.js for the contract):
 *   page.rendered   — once, when the bundle boots (t_ms since navigationStart)
 *   page.engaged    — once, at pagehide, via shared-web/page-engagement
 *   page.restored   — back/forward-cache return (pageshow persisted)
 *   page.error      — window errors / unhandled rejections / render failures
 *   ui.clicked      — the delegated [data-log] listener
 *   ui.seen         — the [data-log-seen] IntersectionObserver (≥50%, once
 *                     per declared entity/context), the exposure half of
 *                     saw→clicked funnels
 *   ui.failed       — trackFailed(): the page observed a failure/refusal/
 *                     degradation (names from registry UI_FAILED_NAMES)
 *   plus whatever a surface passes to track() directly (consent.*, login.*,
 *   page.scrolled via the page-engagement milestone callback).
 */

export type TrackProps = Record<string, string | number | boolean | null | undefined>;

/**
 * The page every beacon is stamped with. A multi-page site declares it once at
 * boot and the browser's own URL stays true for the life of the document; an
 * SPA re-declares it on each client route change.
 *
 * `path` is held rather than read from `window.location` at send time because
 * of ONE caller: the page-engagement flush at a route change runs after the
 * router has already moved, and the summary it sends belongs to the page being
 * LEFT. Holding the identity means the router updates it once, in order
 * (flush, then declare the new page), and every event lands on the right page
 * without any beacon having to carry its own copy.
 */
let currentPage = { surface: '', route: '', path: '' };

/**
 * Set at pagehide, cleared when the document comes back from the back/forward
 * cache. A torn-down document shows nothing to anyone, but its code still runs
 * for a moment: the browser aborts every request it had in flight, each one
 * rejects, and the pages' catch blocks would report those as failures the
 * person met. They met nothing — they left. (prod 2026-09-04: /settings opened
 * in a hidden tab and torn down 112 ms later recorded session read_failed +
 * settings_load load_failed while the server answered /api/me 200 to a
 * connection nobody was on; the same shape behind rapid back-button exits and
 * crawlers.) The two failure reporters stay silent from that moment on.
 * page.engaged is sent from the same pagehide and is not a failure — track()
 * itself is not gated.
 */
let pageGone = false;

/** Declare the page telemetry is currently on. */
export function setTrackingPage(page: { surface: string; route?: string; path?: string }): void {
  currentPage = {
    surface: page.surface || '',
    route: page.route || '',
    path: page.path || '',
  };
}

/** The page telemetry is currently on — what a teardown summary reports about. */
export function trackingPage(): { surface: string; route: string; path: string } {
  return currentPage;
}

function sessionId(): string {
  try {
    const existing = window.sessionStorage.getItem('ember_sid');
    if (existing && /^s_[a-z0-9]{16,32}$/.test(existing)) return existing;
    const minted = `s_${Math.random().toString(36).slice(2, 12)}${Date.now().toString(36)}`;
    window.sessionStorage.setItem('ember_sid', minted);
    return minted;
  } catch {
    return '';
  }
}

function clean(props?: TrackProps): Record<string, string | number | boolean | null> {
  const out: Record<string, string | number | boolean | null> = {};
  if (!props || typeof props !== 'object') return out;
  for (const [k, v] of Object.entries(props)) {
    if (v === undefined) continue;
    out[k] = v as string | number | boolean | null;
  }
  return out;
}

/** Fire-and-forget: telemetry never breaks a page, never blocks navigation. */
export function track(type: string, props?: TrackProps, opts?: { surface?: string; path?: string }): void {
  if (typeof fetch === 'undefined' || typeof window === 'undefined') return;
  // The ui.* rows (clicked / seen / failed) declare `route` in the registry,
  // but until 2026-09-08 nothing put it on the wire — 0 of 1,377 ui.clicked
  // rows in a two-day prod window carried one — so "what was seen / tapped on
  // the homepage" could only be answered by chaining the tab session back to
  // a page arrival. The page the act happened on is a fact of the act; it
  // rides here, from the same page identity the router keeps for page.engaged.
  const withRoute = type.startsWith('ui.') && currentPage.route && props?.route === undefined
    ? { route: currentPage.route, ...props }
    : props;
  void fetch('/api/events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({
      events: [{
        type,
        client_ts: new Date().toISOString(),
        path: opts?.path ?? currentPage.path,
        surface: opts?.surface ?? currentPage.surface,
        session_id: sessionId(),
        props: clean(withRoute),
      }],
    }),
    keepalive: true,
  }).catch(() => {});
}

/** The page broke. kind: error | rejection | render_failed */
export function trackError(kind: string, errorName: string, props?: TrackProps): void {
  if (pageGone) return;
  track('page.error', { kind, error_name: errorName, ...props });
}

const INTERACTIVE_SELECTOR = 'a[href], button, summary, [data-log]';
const NAME_MAX = 48;
let clicksInstalled = false;
let errorsInstalled = false;
let lastClickKey = '';
let lastClickAt = 0;

function slugify(raw: string): string {
  return raw.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, NAME_MAX);
}

/**
 * Controlled entity/context dimensions for declarative product telemetry.
 * Business components may declare these data attributes; this one transport
 * is the only code that turns them into event props. Visible copy and URLs
 * are deliberately never inspected.
 */
const DECLARATIVE_PROP_ATTRIBUTES = Object.freeze({
  'data-log-request-id': 'request_id',
  'data-log-ember-id': 'ember_id',
  'data-log-edge-id': 'edge_id',
  'data-log-conversation-id': 'conversation_id',
  // The letter row a letter control is about (2026-09-18; a share ask's ember, a read, a question, a room invite).
  'data-log-match-id': 'match_id',
  // The read (ember_reads row, §4.9 2026-09-27) a read_* control is about.
  'data-log-read-id': 'read_id',
  // The weekly letter (ember_weekly_letters row, §4.20) a me_letter_* / talk_letter_* element is about (2026-09-23).
  'data-log-letter-id': 'letter_id',
  // The How-it-went page (ember_precedents row, §4.22) a precedent_* control is about (2026-10-05).
  'data-log-precedent-id': 'precedent_id',
  // The one-time token the Android app banner writes into the Play Install
  // Referrer (2026-10-20): joins this browser's tap to the install it starts.
  'data-log-handoff-id': 'handoff_id',
  'data-log-search-id': 'search_id',
  'data-log-discovery': 'discovery',
  'data-log-state': 'state',
  'data-log-resume': 'resume',
  'data-log-position': 'position',
  'data-log-theme': 'theme',
  // The life node (a light of the Banyan, §4.17) an element is about — the
  // dimension every tree_*, place_tell and result_seal_open act rides, and
  // the entry a light-opened TalkSheet carries. Declared in the registry on
  // 2026-09-05; this transport did not carry it until 2026-09-06, so tree
  // rows before that day have no node.
  'data-log-node-id': 'node_id',
  // Experiment arm the element rendered under (today: the Result share
  // section's A/B, §4.8) — the dimension that splits a metric by variant
  // without minting a variant event name.
  'data-log-arm': 'arm',
  // When the element entered the DOM, on the same performance.now() clock as
  // the seen's t_ms — lets one exposure event separate "the data arrived"
  // (mount) from "the person scrolled to it" (t_ms).
  'data-log-mount-t-ms': 'mount_t_ms',
} as const);

function declarativeProps(el: Element): TrackProps {
  const props: TrackProps = {};
  for (const [attribute, key] of Object.entries(DECLARATIVE_PROP_ATTRIBUTES)) {
    const raw = el.getAttribute(attribute);
    if (raw == null || raw === '') continue;
    if (key === 'position' || key === 'mount_t_ms') {
      const value = Number(raw);
      if (Number.isInteger(value) && value > 0) props[key] = value;
    } else if (key === 'resume') {
      // A flag, not a string: the whole point of the prop is a yes/no split,
      // and 'false' is truthy in every reader that forgets to compare.
      props[key] = raw === 'true';
    } else {
      props[key] = raw;
    }
  }
  return props;
}

function interactionKey(name: string, props: TrackProps): string {
  return [
    name,
    props.request_id || '',
    props.ember_id || '',
    props.edge_id || '',
    // Without this a list of same-name cards (My Embers) would report its
    // first card's exposure and swallow every other one.
    props.conversation_id || '',
    props.search_id || props.discovery || '',
    // Position is part of the entity for list telemetry: the load_more
    // sentinel fires once per PAGE it loads, not once per document, and a
    // positioned card's exposure is a different fact from its neighbor's.
    props.position || '',
    // So is state: page_failure(state=not_found) after page_failure
    // (state=load_failed) is a second fact, and a card seen in two lifecycle
    // states was seen twice.
    props.state || '',
    // And the light: two lights' tags opened in one visit are two exposures
    // of tree_light_open, not one — without this the second light and every
    // later one would be swallowed.
    props.node_id || '',
    // The same for a letter, a read and a match (2026-10-12): two letters
    // laid open in one visit are two exposures. The apps key on the same
    // slots (TrackSession interaction keys), so a seen counts alike on all three.
    props.letter_id || '',
    props.read_id || '',
    props.match_id || '',
  ].join(':');
}

/**
 * ui.failed — the page observed a failure, refusal, or degradation. `name` is
 * the operation (registry UI_FAILED_NAMES), `reason` the specific cause: the
 * API error code when the server refused, a client cause when it never got
 * there. Call it where the page DECIDES to show (or absorb) the failure —
 * never inside retry loops or background polls. Silent once the document has
 * been torn down (see pageGone): a request the browser aborted because the
 * person left is not a failure the page showed them.
 */
export function trackFailed(name: string, reason: string, props?: TrackProps): void {
  if (pageGone) return;
  track('ui.failed', { name, reason: String(reason || 'unknown').slice(0, 120), ...props });
}

/**
 * The delegated click listener — the only source of ui.clicked. Only explicit
 * `data-log` values become names; visible text, ARIA labels and URLs never do
 * (they can carry user data or copy that changes under a series).
 */
export function initClickTracking(): void {
  if (clicksInstalled || typeof document === 'undefined') return;
  clicksInstalled = true;
  document.addEventListener('click', (event) => {
    try {
      const target = event.target as Element | null;
      const el = target?.closest?.(INTERACTIVE_SELECTOR);
      if (!el) return;
      const name = slugify(el.getAttribute('data-log') || '');
      if (!name) return;
      // A label tap re-dispatches a synthetic click on its wrapped input;
      // both resolve closest() to the same element. One tap = one event.
      const props: TrackProps = { name, ...declarativeProps(el) };
      const key = interactionKey(name, props);
      const now = Date.now();
      if (key === lastClickKey && now - lastClickAt < 400) return;
      lastClickKey = key;
      lastClickAt = now;
      // FAQ <summary>: in capture phase details.open is still the pre-toggle
      // state, so !open means this click is opening it.
      if (el.tagName === 'SUMMARY') {
        const details = el.closest('details');
        if (details) props.opening = !details.open;
      }
      track('ui.clicked', props);
    } catch { /* telemetry never breaks a click */ }
  }, { capture: true });
}

let seenInstalled = false;
const seenNames = new Set<string>();

/**
 * ui.seen — an instrumented element entered the viewport (≥50%), once per
 * declared entity/context per visit. Static controls with no entity props
 * retain the old once-per-name behavior. Opt-in via `data-log-seen` (names
 * share ui.clicked's registry vocabulary), so exposure is a deliberate
 * measurement, never ambient DOM capture. A MutationObserver picks up
 * elements mounted after boot (sticky bars, dialogs) and names put on
 * elements already standing (a card that arrives as an unnamed slot and is
 * named once it has landed).
 */
export function initSeenTracking(): void {
  if (seenInstalled || typeof document === 'undefined' || typeof IntersectionObserver === 'undefined') return;
  seenInstalled = true;
  const io = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      try {
        if (entry.intersectionRatio < 0.5) continue;
        const name = slugify(entry.target.getAttribute('data-log-seen') || '');
        io.unobserve(entry.target);
        const props = declarativeProps(entry.target);
        const key = interactionKey(name, props);
        if (!name || seenNames.has(key)) continue;
        seenNames.add(key);
        let t: number | null = null;
        try { t = Math.round(performance.now()); } catch { /* t stays null */ }
        track('ui.seen', { name, t_ms: t, ...props });
      } catch { /* telemetry never breaks a page */ }
    }
  }, { threshold: 0.5 });
  const observeAll = (root: ParentNode) => {
    root.querySelectorAll?.('[data-log-seen]').forEach((el) => io.observe(el));
  };
  observeAll(document);
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes') {
          const el = mutation.target;
          if (el instanceof Element && el.hasAttribute('data-log-seen')) io.observe(el);
          continue;
        }
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.hasAttribute('data-log-seen')) io.observe(node);
          observeAll(node);
        });
      }
    }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-log-seen'] });
  }
}

/**
 * Is this error ours to answer for?
 *
 * An in-app browser injects its own script into every page it opens, and that
 * script's exceptions arrive at OUR window.onerror. The Facebook Android app's
 * `iabjs://navigation_performance_logger_android` is the loud one: it calls
 * postMessage into a Java object the app has already collected, throwing
 * "Error invoking postMessage: Java object is gone" on a page that is working
 * perfectly for the person reading it (prod 2026-08-26→09-07: 74 visitors in
 * one week and rising, the second-largest source of page.error, none of them a
 * failure anyone met). Counting it made the error tab a measure of how much
 * paid Android traffic we bought.
 *
 * Two shapes are refused, both of them by definition not our bundle:
 *   - a filename that is not this origin — `iabjs://…`, an extension's
 *     `chrome-extension://…`, any third-party script
 *   - the empty cross-origin report the browser gives instead of details
 *     ("Script error." with no filename), which carries nothing to act on
 *
 * An error from our own inline script keeps its filename (the page URL) and is
 * still reported. Everything the bundle throws is same-origin and untouched.
 */
function isOurScript(filename: string, message: string): boolean {
  const source = String(filename || '').trim();
  if (!source) return String(message || '').trim() !== 'Script error.';
  try {
    const base = window.location.href || window.location.origin;
    return new URL(source, base).origin === window.location.origin;
  } catch {
    return false; // an opaque scheme (iabjs://…) is never ours
  }
}

/** window error / unhandledrejection → page.error. Render failures are the
 * error boundary's to report via trackError('render_failed', …). */
export function initErrorTracking(): void {
  if (errorsInstalled || typeof window === 'undefined') return;
  errorsInstalled = true;
  window.addEventListener('error', (event) => {
    try {
      if (!isOurScript(event.filename, event.message)) return;
      trackError('error', event.error?.name || 'Error', {
        message: String(event.message || '').slice(0, 300),
        stack: String(event.error?.stack || '').slice(0, 500),
        source: String(event.filename || '').slice(0, 256),
        line: Number.isFinite(event.lineno) ? event.lineno : null,
        column: Number.isFinite(event.colno) ? event.colno : null,
      });
    } catch { /* never breaks the page */ }
  });
  window.addEventListener('unhandledrejection', (event) => {
    try {
      const reason = event.reason as { name?: string; message?: string } | undefined;
      trackError('rejection', (reason && reason.name) || 'UnhandledRejection', {
        message: String((reason && reason.message) || '').slice(0, 300),
      });
    } catch { /* never breaks the page */ }
  });
}

/**
 * Boot-time init for a bundle: sets the default surface, reports
 * page.rendered, installs clicks + errors. page.engaged is wired by the
 * caller through shared-web/page-engagement (it owns the scroll math):
 *
 *   initPageEngagement({
 *     onScrollDepth: () => {},
 *     onEngagement: (s) => track('page.engaged', {
 *       route: trackingPage().route, visit_id: s.visit_id,
 *       dwell_ms: s.dwell_ms, visible_ms: s.visible_ms,
 *       max_scroll_pct: s.max_scroll_pct, clicks: s.clicks,
 *     }),
 *   })
 */
/**
 * The same-origin referrer's pathname, or undefined. This is the from-page of
 * a cross-document move INSIDE the site — the only record a full document load
 * keeps of which of our pages handed the visitor over (the SPA's in-document
 * moves carry from_route on page.navigated instead). Cross-origin referrers
 * stay host-only (renderProps.referrer_host, marketing bundle) — a third-party
 * path is acquisition context we deliberately do not collect.
 */
function sameOriginReferrerPath(): string | undefined {
  try {
    if (!document.referrer) return undefined;
    const ref = new URL(document.referrer);
    if (ref.origin !== window.location.origin) return undefined;
    return ref.pathname;
  } catch {
    return undefined;
  }
}

export function initTracking({ surface, route = '', renderProps = {} }: { surface: string; route?: string; renderProps?: TrackProps }): void {
  setTrackingPage({ surface, route, path: window.location.pathname || '/' });
  initClickTracking();
  initErrorTracking();
  initSeenTracking();
  // Back/forward-cache return: the page is on screen again with no GET and no
  // bundle boot — the re-view browsers otherwise hide.
  window.addEventListener('pageshow', (event) => {
    try {
      pageGone = false;
      if ((event as PageTransitionEvent).persisted) track('page.restored', {});
    } catch { /* never breaks the page */ }
  });
  window.addEventListener('pagehide', () => { pageGone = true; });
  // A timing that cannot be a page-load timing (bfcache restore, suspended
  // webview) is reported as null rather than as a number — one absurd row can
  // move an average by an order of magnitude.
  let t: number | null = null;
  try {
    const value = Math.round(performance.now());
    if (Number.isFinite(value) && value >= 0 && value <= 60_000) t = value;
  } catch { /* t stays null */ }
  track('page.rendered', { t_ms: t, referrer_path: sameOriginReferrerPath(), ...renderProps });
}
